import React, { useEffect, useRef } from 'react';
import { Box, TextField, FormControlLabel, RadioGroup, Radio, Checkbox, Typography, Card, CardContent, Grid, Divider } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CompanyDetails, CompanyEntity } from '../../../graphql';
import dayjs from 'dayjs';
import { calculateExpiryDays } from '../utils/calculateExpiryDays';
import { GstDetailsValidationErrors } from '../types';

interface Props {
  companyDetails: CompanyDetails;
  companyEntity: CompanyEntity;
  startYear: number;
  endYear: number;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnBlurGSTDetails: (event: React.FocusEvent<HTMLInputElement>) => void;
  error: GstDetailsValidationErrors;
}

const registrationTypeMap: Record<number, string> = {
  1: 'Unregistered',
  2: 'Registered',
  3: 'Composition',
  4: 'SEZ',
};

const periodicityMap: Record<number, string> = {
  1: 'Monthly',
  2: 'Quarterly',
};

export const GSTDetailsStep: React.FC<Props> = React.memo(
  ({ companyDetails, companyEntity, handleChange, startYear, endYear, error, handleOnBlurGSTDetails }) => {
    const isUnregistered = Number(companyDetails?.registrationtype) === 1;
    const isComposition = Number(companyDetails?.registrationtype) === 3;
    const gstinRef = useRef<HTMLInputElement>(null);
    const lutRef = useRef<HTMLInputElement>(null);

    const expiry = calculateExpiryDays(companyDetails?.lutbondvalidfrom, companyDetails?.lutbondtodate);

    useEffect(() => {
      if(error.gstin) {
        gstinRef.current?.focus();
      } else if(error.lutNumber) {
        lutRef.current?.focus();
      }
    }, [error]);

    return (
      <Box p={2}>
        <Card variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '20px', sm: '21px' } }}>
              {`GST Settings (${startYear} - ${endYear})`}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6} lg={3}>
                <TextField
                  fullWidth
                  required={Number(companyDetails?.registrationtype) !== 1}
                  label="GSTIN (if any)"
                  name="gstin"
                  value={companyEntity?.gstin || ''}
                  onChange={handleChange}
                  size="small"
                  error={!!error.gstin}
                  helperText={error.gstin}
                  inputProps={{ style: { letterSpacing: '1.5px', fontSize: '15px' } }}
                  inputRef={gstinRef}
                />
              </Grid>
            </Grid>

            <Box mt={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '16px', sm: '18px' }, color: '#666666' }}>
                Registration Type
              </Typography>
              <RadioGroup
                row
                name="registrationtype"
                value={companyDetails?.registrationtype?.toString() || ''}
                onChange={handleChange}
                sx={{ mb: 1 }}
              >
                {Object.entries(registrationTypeMap).map(([key, label]) => (
                  <FormControlLabel key={key} value={key} control={<Radio />} label={label} />
                ))}
              </RadioGroup>

              {!isUnregistered && !isComposition && (
                <FormControlLabel
                  control={<Checkbox name="einvoiceflag" onChange={handleChange} checked={companyDetails?.einvoiceflag === 1} />}
                  label="Enable E‑Invoice"
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {!isUnregistered && (
          <>
            <Card variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '16px', sm: '18px' }, color: '#666666' }}>
                  Periodicity of GST
                </Typography>
                <RadioGroup row name="gstrperiod" value={companyDetails?.gstrperiod?.toString() || ''} onChange={handleChange}>
                  {Object.entries(periodicityMap).map(([key, label]) => (
                    <FormControlLabel key={key} value={key} control={<Radio />} label={label} />
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '16px', sm: '18px' }, color: '#666666' }}>
                  LUT Bond Details
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="flex-start" justifyContent="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="LUT Bond Number"
                      name="lut"
                      value={companyDetails?.lut ?? ''}
                      onBlur={handleOnBlurGSTDetails}
                      onChange={handleChange}
                      error={!!error.lutNumber}
                      helperText={error.lutNumber}
                      inputRef={lutRef}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} sx={{ pr: { xs: 0, md: 0 } }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Valid From"
                        name="lutbondvalidfrom"
                        format="DD/MM/YYYY"
                        disabled={!companyDetails?.lut}
                        value={companyDetails?.lutbondvalidfrom ? dayjs.unix(companyDetails.lutbondvalidfrom) : null}
                        onChange={(date) =>
                          handleChange({
                            target: {
                              name: 'lutbondvalidfrom',
                              value: date ? Math.floor(date.toDate().getTime() / 1000) : '',
                            },
                          } as React.ChangeEvent<HTMLInputElement>)
                        }
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            error: !!error.lutFrom,
                            helperText: error.lutFrom,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} sx={{ pl: { xs: 0, md: 0 } }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Valid To"
                        name="lutbondtodate"
                        format="DD/MM/YYYY"
                        disabled={true}
                        value={companyDetails?.lutbondtodate ? dayjs.unix(companyDetails.lutbondtodate) : null}
                        onChange={(date) =>
                          handleChange({
                            target: {
                              name: 'lutbondtodate',
                              value: date ? Math.floor(date.toDate().getTime() / 1000) : '',
                            },
                          } as React.ChangeEvent<HTMLInputElement>)
                        }
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            error: !!error.lutTo,
                            helperText: error.lutTo,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      height="100%"
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="flex-start"
                      pl={{ xs: 0, sm: 1 }}
                      mt={1}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      <Typography
                        sx={{
                          color: expiry.status === 'Expired' ? 'red' : '#000',
                          fontSize: { xs: '12px', sm: '14px' },
                        }}
                      >
                        Expires:&nbsp;
                        {companyDetails?.lut ? (expiry.difference !== null ? `${expiry.difference} Days` : 'N/A Days') : 'N/A Days'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    );
  }
);
